import {
  BIOMETRY_UNICO_PARAM_GROUP,
  CACHE_CONSULTAS_UNICO_PARAMETER,
  DEFAULT_BIOMETRY_REUSE_CACHE_DAYS,
  MINUTES_PER_DAY,
  UNICO_FLOW_PROCESS_RESULT_TYPE
} from "@repo/core/constants/biometry-reuse-wayup";
import type {
  BiometryValidityParamRowDTO,
  UnicoStagePerformedRowDTO
} from "@repo/core/services/entities/WayupRequisitions";

function parseStageTimestampMs(row: UnicoStagePerformedRowDTO): number {
  const datePart = (row.dataconsulta || "").trim();
  const timePart = (row.horaconsulta || "").trim();
  if (!datePart) {
    return 0;
  }
  const iso = timePart ? `${datePart}T${timePart}` : datePart;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? 0 : ms;
}

function sortStagesNewestFirst(
  rows: UnicoStagePerformedRowDTO[]
): UnicoStagePerformedRowDTO[] {
  return [...rows].sort(
    (a, b) => parseStageTimestampMs(b) - parseStageTimestampMs(a)
  );
}

function nonemptyProcessOid(
  row: UnicoStagePerformedRowDTO
): string | undefined {
  const id = String(row.processoid ?? "").trim();
  return id !== "" ? id : undefined;
}

/**
 * Reuse only when there is at least one "Resultado do Processo".
 * If several, pick the newest by dataconsulta + horaconsulta.
 */
export function pickProcessOidFromUnicoStages(
  rows: UnicoStagePerformedRowDTO[] | null | undefined
): string | undefined {
  if (!rows?.length) {
    return undefined;
  }

  const sorted = sortStagesNewestFirst(rows);
  const resultFlow = sorted.filter(
    (r) => r.tipodefluxo.trim() === UNICO_FLOW_PROCESS_RESULT_TYPE
  );

  if (resultFlow.length === 0) {
    return undefined;
  }

  for (const row of resultFlow) {
    const id = nonemptyProcessOid(row);
    if (id) {
      return id;
    }
  }

  return undefined;
}

/**
 * Parses day count from busca-prazo-validade-bio rows for CACHE_CONSULTAS_UNICO.
 */
export function parseCacheConsultasDays(
  rows: BiometryValidityParamRowDTO[] | null | undefined,
  grupoFilter: string = BIOMETRY_UNICO_PARAM_GROUP,
  parametroFilter: string = CACHE_CONSULTAS_UNICO_PARAMETER
): number {
  const match = rows?.find(
    (r) =>
      r.grupo?.trim() === grupoFilter &&
      r.parametro?.trim() === parametroFilter
  );
  const raw = match?.valor?.trim();
  if (!raw) {
    return DEFAULT_BIOMETRY_REUSE_CACHE_DAYS;
  }

  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n) || n < 1) {
    return DEFAULT_BIOMETRY_REUSE_CACHE_DAYS;
  }

  return n;
}

export function daysToLimiteMinutos(days: number): number {
  const d = Math.max(1, Math.floor(days));
  return Math.max(1, d * MINUTES_PER_DAY);
}
